/// <reference path="../types.d.ts" />

import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { CaseDocuments } from './action-types';
import { api } from '../core/net';
import {
  caseUtils,
  appServiceLines,
  authenticationChannel,
  objectUtils,
  documentUtils
} from '@codametrix/ui-common';
import * as commonApi from '@codametrix/client-api';

const { AutomateGeneration } = appServiceLines;

type CaseAndProcess = {
  caseDetail: CMxAPI.Case;
  activeProcess: CMxAPI.CaseRun;
};

interface Dictionary<T> {
  [index: string]: T;
}

const caseDetailAvailable = createAction<CaseAndProcess>(
  CaseDocuments.CASE_DETAIL
);

const docMetadataAvailable = createAction<CMxAPI.DocumentMetadata[]>(
  CaseDocuments.DOCUMENT_METADATA
);

const documentAvailable = createAction<AppProps.DocumentWithMetadata>(
  CaseDocuments.NOTE
);

type CaseIds = {
  caseId: string;
  processId: string;
};

const getDocuments = async (
  caseRun: CMxAPI.CaseRun,
  tenantInfo: CMxAPI.Organization,
  automateGen?: string
): Promise<CMxAPI.DocumentMetadataWithNotices[]> => {
  let filterParams: {
    tenantId: string;
    businessKey?: string;
    fillerOrderNumber?: string;
  }[];

  const isIPCServiceLine = appServiceLines.isIPC(caseRun.service_line);
  const isSurgeryServiceLine = appServiceLines.isSurgery(caseRun.service_line);

  if (isIPCServiceLine || isSurgeryServiceLine) {
    const docsSeen = new Set<string>();
    filterParams = (caseRun.notes as CMxAPI.Note[])
      ?.filter(note => note.unique_document_number)
      .map(note => {
        const docId = note.unique_document_number as string;
        docsSeen.add(docId);
        return {
          tenantId: `${tenantInfo.tenantId}`,
          uniqueDocumentNumber: docId
        };
      });
  } else if (automateGen === AutomateGeneration.GEN3) {
    filterParams = [
      {
        tenantId: `${tenantInfo.tenantId}`,
        fillerOrderNumber: caseRun.order_filler_number.number ?? ''
      },
      {
        tenantId: `${tenantInfo.tenantId}`,
        businessKey: caseRun.caserun_uid ?? ''
      }
    ];
  } else {
    filterParams = [
      {
        tenantId: `${tenantInfo.tenantId}`,
        fillerOrderNumber: caseRun.order_filler_number.number
      }
    ];
  }

  // docs metadata filter
  const response: CMxAPI.DocumentMetadataWithNotices[] = [];
  let docs = await fetchDocumentsByFilterParams(filterParams);

  if (docs.length === 0 && isIPCServiceLine) {
    // if no docs found, try again with businessKey
    filterParams = buildBusinessKeyFilters(caseRun, tenantInfo);
    docs = await fetchDocumentsByFilterParams(filterParams);
  }
  // add documentName to each doc
  if (isIPCServiceLine || isSurgeryServiceLine) {
    docs = docs.map(doc => ({
      ...doc,
      documentName: doc?.noteSpecialty ?? 'Document'
    }));
  }
  if (
    automateGen === AutomateGeneration.GEN3 ||
    isIPCServiceLine ||
    isSurgeryServiceLine
  ) {
    const { MimeTypes } = documentUtils;
    const docGroupProperty = isIPCServiceLine
      ? 'uniqueDocumentNumber'
      : 'externalId';
    const groupedDocs: Dictionary<CMxAPI.DocumentMetadata[]> = objectUtils.groupBy(
      docs,
      docGroupProperty
    );

    Object.entries(groupedDocs).forEach(([externalId, docs]) => {
      // group documents by mimeType
      const groupByDocs: Dictionary<CMxAPI.DocumentMetadata[]> = objectUtils.groupBy(
        docs,
        'mimeType'
      );
      const precedence = [MimeTypes.HTML, MimeTypes.PDF, MimeTypes.TEXT];
      const doc = documentUtils.firstAvailable(groupByDocs, precedence);

      if (doc) {
        // check if conversion is expected
        // add to response if conversion is expected and missing
        const conversionTargets = documentUtils.getExpectedConversionTargets(
          groupByDocs
        );
        const foundConversion = conversionTargets.includes(doc.mimeType);

        response.push({
          ...doc,
          missedConversion: conversionTargets.length > 0 && !foundConversion
        });
      }
    });
  } else {
    response.push(...docs);
  }

  return response;
};

const buildBusinessKeyFilters = (
  caseRun: CMxAPI.CaseRun,
  tenantInfo: CMxAPI.Organization
) => {
  const filterParams = [
    {
      tenantId: `${tenantInfo.tenantId}`,
      businessKey: caseRun.caserun_uid ?? ''
    }
  ];
  caseRun.child_case_list?.forEach(childCase => {
    filterParams.push({
      tenantId: `${tenantInfo.tenantId}`,
      businessKey: childCase.caserun_uid ?? ''
    });
  });
  return filterParams;
};

const fetchDocumentsByFilterParams = async (
  filterParams: {
    tenantId: string;
    businessKey?: string;
    fillerOrderNumber?: string;
  }[]
) => {
  const documentRequests = filterParams.map(filterParam => {
    //@ts-ignore
    const docFilter = new URLSearchParams(filterParam);
    return api<CMxAPI.DocumentMetadata[]>({
      endpoint: `/documents/v2?${docFilter}`
    });
  });

  // get list of documents
  const allPromises = await Promise.all<CMxAPI.DocumentMetadata[]>(
    documentRequests
  );
  const docs = allPromises.flatMap(doc => doc);
  return docs;
};

const fetchDocument = createAsyncThunk<
  AppProps.DocumentWithMetadata,
  CMxAPI.DocumentMetadata,
  { rejectValue: CMxCommonApp.SubmitError }
>(CaseDocuments.FETCH_DOCUMENT, async (metadata, { dispatch }) => {
  const endpoint = `/documents/download/${metadata.documentId}${
    metadata.mimeType === 'application/pdf' ? '/encoded' : ''
  }/v1`;
  const content = await commonApi.request(endpoint);
  const note: CMxAPI.DocumentServiceNote = { Note: await content.text() };
  const docWithMetadata = { note, metadata };
  dispatch(documentAvailable(docWithMetadata));

  return docWithMetadata;
});

const fetchCaseRunDocuments = createAsyncThunk<
  CMxAPI.DocumentMetadata[],
  CMxAPI.CaseRun,
  { rejectValue: CMxCommonApp.SubmitError }
>(CaseDocuments.FETCH_CASERUN_DOCUMENTS, async (activeCaseRun, { dispatch }) => {
  const tenantInfo = await api<CMxAPI.Organization>({
    endpoint: `/organization/by-name/${activeCaseRun.tenant_name}/v1`
  });

  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: activeCaseRun.service_line,
    caseIdentifier: activeCaseRun.case_uid ?? ''
  });
  const automateGen = serviceLineConfig.automateGen;

  let docs = await getDocuments(activeCaseRun, tenantInfo, automateGen);

  const noteRelated = new Set(['HL7', 'NOTE']);

  const isIPCServiceLine = appServiceLines.isIPC(activeCaseRun.service_line);
  const isSurgeryServiceLine = appServiceLines.isSurgery(
    activeCaseRun.service_line
  );
  docs = docs.filter(doc => {
    let documentMatch = true;
    if (isIPCServiceLine || isSurgeryServiceLine) {
      const notes = activeCaseRun?.notes as CMxAPI.Note[] | undefined;
      documentMatch =
        notes?.some(
          note => note.unique_document_number === doc.uniqueDocumentNumber
        ) ?? false;
    } else if (
      noteRelated.has(doc.documentCategory) &&
      activeCaseRun.external_id !== null &&
      doc.externalId !== null
    ) {
      documentMatch = doc.externalId === activeCaseRun.external_id;
    }
    return documentMatch;
  });

  dispatch(docMetadataAvailable(docs));
  const docGroups = objectUtils.groupBy(docs, 'documentCategory');
  const docCategories = [
    'HL7',
    'DFT_IN',
    'DFT_OUT',
    'DFT_EHR',
    'DFT_TRACK',
    'NOTE',
    'PREPROCESSED_MESSAGE',
    'EVIDENCE',
    'CASE_JSON'
  ];
  let categories = docCategories.filter(
    group => objectUtils.get(docGroups, group)?.length
  );
  if (!categories.length) {
    categories = Object.keys(docGroups).filter(
      group => objectUtils.get(docGroups, group)?.length
    );
  }

  if (categories.length) {
    const [firstDocGroup] = categories;
    const [metadata] = objectUtils.get(docGroups, firstDocGroup);
    await dispatch(fetchDocument(metadata)).unwrap();
  }
  return docs;
});

const loadDocuments = createAsyncThunk<
  CMxAPI.DocumentMetadata[],
  CaseIds,
  { rejectValue: CMxCommonApp.SubmitError }
>(CaseDocuments.LOAD, async (caseIds, { dispatch }) => {
  await authenticationChannel.ready;

  let { caseId, processId } = caseIds;

  const url = new URL(window.location.toString());
  const serviceLine = url.searchParams.get('serviceLine') ?? '';
  const caseIdentifier = url.searchParams.get('caseId') ?? '';
  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: serviceLine,
    caseIdentifier
  });

  const caseParams = new URLSearchParams(serviceLineConfig.caseDetailParms);

  const expandedCaseDetail: CMxAPI.Case = await api<CMxAPI.Case>({
    endpoint: `${serviceLineConfig.path}/caseinstance/${caseId}/details/${
      serviceLineConfig.caseDetailVersion
    }?${caseParams.toString()}`
  });

  const processIdNumber = parseInt(processId);
  const activeCaseRun = caseUtils.getCaseRun(
    expandedCaseDetail,
    processIdNumber
  );

  const caseDetailAndProcess = {
    caseDetail: expandedCaseDetail,
    activeProcess: activeCaseRun
  };

  dispatch(caseDetailAvailable(caseDetailAndProcess));
  return await dispatch(fetchCaseRunDocuments(activeCaseRun)).unwrap();
});

export {
  loadDocuments,
  caseDetailAvailable,
  docMetadataAvailable,
  documentAvailable,
  fetchDocument,
  fetchCaseRunDocuments,
  getDocuments
};
